import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { ArrowReturn } from '@swyftx/aviary/icons/outlined';

import { MarginInstrument } from '@routes/SwyftxMargin/components/General';
import { capitalize } from '@utils/formatting';

import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions';

import { MarginPositionRowType } from '../MarginPositionsTable.data';

type Props = {
  positionId?: string;
  positionType: MarginPositionRowType;
};

const MarginPositionsTableInstrumentSide: React.FC<Props> = observer(({ positionId, positionType }) => {
  const { t } = useTranslation('margin');
  const { position, positionMarket, side, type, isOpen } = useMarginPositionDetails({ positionId });

  const leadingElement = useMemo(() => {
    if (positionType === 'position')
      return <MarginInstrument market={positionMarket} size='medium' showMarketSymbol={false} />;

    return (
      <FlexLayout alignItems='center' justifyContent='center' className='w-40'>
        <ArrowReturn className='text-color-text-primary' />
      </FlexLayout>
    );
  }, [positionMarket, positionType]);

  const title = useMemo(() => {
    switch (positionType) {
      case 'stoploss':
        return (
          <Body weight='bold' className='whitespace-nowrap' color='primary'>
            {t('protectionOrders.stopLoss')}
          </Body>
        );
      case 'takeprofit':
        return (
          <Body weight='bold' className='whitespace-nowrap' color='primary'>
            {t('protectionOrders.takeProfit')}
          </Body>
        );
      case 'position':
        if (side === undefined) return null;

        // Make the side text lowercase if we are appending limit or stop to the front
        let tradeSide = isOpen ? t(`tradeSide.${side}`) : t(`tradeSide.${side}`).toLowerCase();

        return (
          <Body weight='bold' className='whitespace-nowrap' color='primary'>
            {!isOpen && t(`tradeType.${type}`)} {tradeSide}
          </Body>
        );
      default:
        positionType satisfies never;
    }
  }, [isOpen, positionType, side, t, type]);

  const subTitle = useMemo(
    () =>
      position ? (
        <Body size='small' color='secondary' className='whitespace-nowrap'>
          {capitalize(position.status)} •{' '}
          {DateTime.fromMillis(position.opened || position.lastUpdate || 0)
            .toFormat('hh:mm a')
            .toUpperCase()}
        </Body>
      ) : null,
    [position],
  );

  if (!position) return null;

  return (
    <FlexLayout justifyContent='start' alignItems='center' spacing={16}>
      {leadingElement}
      <FlexLayout direction='column'>
        {title}
        {subTitle}
      </FlexLayout>
    </FlexLayout>
  );
});

export { MarginPositionsTableInstrumentSide };
